/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import {
    ClusterCtlProtoDeployClusterReq,
    ClusterCtlProtoDestroyClusterReq,
    ClusterCtlProtoGetAccessLevelsReq,
    ClusterCtlProtoGetClusterDeploymentOptionsReq,
    ClusterCtlProtoGetClusterPresetPriceReq,
    ClusterCtlProtoGetClusterPriceReq,
    ClusterCtlProtoGetClusterReq,
    ClusterCtlProtoGetClusterStorageUsageReq,
    ClusterCtlProtoGetLatestCompatibleTypeDBReq,
    ClusterCtlProtoListSnapshotsReq,
    ClusterCtlProtoListClusterLogsReq,
    ClusterCtlProtoListServerLogsReq,
    ClusterCtlProtoListServersReq,
    ClusterCtlProtoReq,
    ClusterCtlProtoResetUserPasswordReq,
    ClusterCtlProtoRestoreClusterReq,
    ClusterCtlProtoResumeClusterReq,
    ClusterCtlProtoSetupClusterReq,
    ClusterCtlProtoPauseClusterReq,
    ClusterCtlProtoUpdateClusterReq,
    ClusterCtlProtoUpdateToLatestCompatibleTypeDBReq,
    ClusterCtlProtoTakeSnapshotReq,
    ClusterCtlProtoDeleteSnapshotReq,
    ClusterCtlProtoCheckClusterAutoPauseReq,
    ClusterCtlProtoMarkClusterAsActiveReq,
    ClusterCtlProtoCloneClusterReq,
} from "../../../application/protocol/cluster-controller";
import { ApiResponse, responseOf } from "../../concept/api-response";
import { Ok, PartialWithUuid } from "../../concept/base";
import {
    Cluster, clusterAutoPauseInformationOf, ClusterLogBatch, clusterLogBatchOf,
    ClusterPrice, clusterPriceOf,
    clusterProtoOf,
    partialClusterOf,
    Server,
    ServerLogBatch,
    serverLogBatchOf,
    serverOf, snapshotOf
} from "../../concept/cluster";
import { ClusterDeploymentOptions, clusterDeploymentOptionsOf } from "../../concept/cluster-deployment-options";
import { AccessLevel, accessLevelOf } from "../../concept/iam";
import { stringToBytes } from "../../util";
import { ApiService } from "../backend/api.service";
import { map, Observable, Subject, takeWhile } from "rxjs";
import { Injectable } from "@angular/core";
import { UnsubListener } from "../backend/cloud-api-backend.service";

@Injectable({
    providedIn: "root",
})
export class ClusterApi {
    constructor(private api: ApiService) {}

    getAccessLevels(clusterUuids: string[]): Observable<AccessLevel[]> {
        return this.api.clusterReqRes(new ClusterCtlProtoReq({ getAccessLevels: new ClusterCtlProtoGetAccessLevelsReq({ clusterUuids: clusterUuids.map(stringToBytes) }) }))
            .pipe(map((res) => res.getAccessLevels.accessLevels.map(accessLevelOf)));
    }

    getClusterDeploymentOptions(props: { teamUuid: string, providerID: string | null, regionID: string | null }): Observable<ClusterDeploymentOptions> {
        return this.api.clusterReqRes(new ClusterCtlProtoReq({ getClusterDeploymentOptions: new ClusterCtlProtoGetClusterDeploymentOptionsReq({
                teamUuid: stringToBytes(props.teamUuid),
                providerId: props.providerID ?? undefined,
                regionId: props.regionID ?? undefined,
            }) }))
            .pipe(map((res) => clusterDeploymentOptionsOf(res.getClusterDeploymentOptions)));
    }

    getLatestCompatibleTypeDB(compatibleWith: string): Observable<{ version: string, migrationRequired: boolean }> {
        return this.api.clusterReqRes(new ClusterCtlProtoReq({
            getLatestCompatibleTypedb: new ClusterCtlProtoGetLatestCompatibleTypeDBReq({
                typedbVersion: compatibleWith
            })
        })).pipe(map((res) => ({
            version: res.getLatestCompatibleTypedb.typedbVersion,
            migrationRequired: res.getLatestCompatibleTypedb.migrationRequired,
        })));
    }

    deployCluster(cluster: Partial<Cluster>, createFromSnapshot: string | undefined): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            deployCluster: new ClusterCtlProtoDeployClusterReq({
                cluster: clusterProtoOf(cluster),
                createFromSnapshot
            })
        }));
    }

    getCluster(props: { clusterId: string, spaceUuid: string }, unsub$: UnsubListener): Observable<ApiResponse<Cluster>> {
        return this.api.clusterReqSub(new ClusterCtlProtoReq(
            { getCluster: new ClusterCtlProtoGetClusterReq({ clusterId: props.clusterId, spaceUuid: stringToBytes(props.spaceUuid) }) }
        ), unsub$).pipe(
            map((res) => responseOf(res.type, partialClusterOf(res.data.getCluster.cluster)))
        );
    }

    updateCluster(cluster: PartialWithUuid<Cluster>): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq(
            { updateCluster: new ClusterCtlProtoUpdateClusterReq({ cluster: clusterProtoOf(cluster) }) }
        ));
    }

    updateToLatestCompatibleTypeDB(clusterUuid: string): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq(
            { updateToLatestCompatibleTypedb: new ClusterCtlProtoUpdateToLatestCompatibleTypeDBReq({ clusterUuid: stringToBytes(clusterUuid) }) }
        ));
    }

    pauseCluster(clusterUuid: string): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq(
            { pauseCluster: new ClusterCtlProtoPauseClusterReq({ clusterUuid: stringToBytes(clusterUuid) }) }
        ));
    }

    resumeCluster(clusterUuid: string): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq(
            { resumeCluster: new ClusterCtlProtoResumeClusterReq({ clusterUuid: stringToBytes(clusterUuid) }) }
        ));
    }

    destroyCluster(clusterUuid: string): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq(
            { destroyCluster: new ClusterCtlProtoDestroyClusterReq({ clusterUuid: stringToBytes(clusterUuid) }) }
        ));
    }

    getClusterPriceThousandthCentsPerHour(props: { cluster: Partial<Cluster>; teamUuid: string }): Observable<ClusterPrice> {
        return this.api.clusterReqRes(new ClusterCtlProtoReq(
            { getClusterPrice: new ClusterCtlProtoGetClusterPriceReq({ teamUuid: stringToBytes(props.teamUuid), cluster: clusterProtoOf(props.cluster) }) }
        )).pipe(map(res => clusterPriceOf(res.getClusterPrice.price)));
    }

    getClusterPresetPriceThousandthCentsPerHour(props: { presetId: string; regionId: string; teamUuid: string; singleNode: boolean }): Observable<ClusterPrice> {
        return this.api.clusterReqRes(new ClusterCtlProtoReq({
            getClusterPresetPrice: new ClusterCtlProtoGetClusterPresetPriceReq({
                teamUuid: stringToBytes(props.teamUuid), regionId: props.regionId, presetId: props.presetId, getForSingleNode: props.singleNode
            })
        })).pipe(map(res => clusterPriceOf(res.getClusterPresetPrice.price)));
    }

    listServers(clusterUuid: string): Observable<Server[]> {
        return this.api.clusterReqRes(new ClusterCtlProtoReq(
            { listServers: new ClusterCtlProtoListServersReq({ clusterUuid: stringToBytes(clusterUuid) }) }
        )).pipe(map(res => res.listServers.servers.map(serverOf)));
    }

    listServerLogs(
        props: { clusterUuid: string, serverUuid: string, serverId: number, initialLineCount: number },
        unsub$: UnsubListener
    ): Observable<ServerLogBatch> {
        return this.api.clusterReqSub(new ClusterCtlProtoReq({
            listServerLogs: new ClusterCtlProtoListServerLogsReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                serverUuid: stringToBytes(props.serverUuid),
                serverIndex: props.serverId,
                initialLineCount: props.initialLineCount
            })
        }), unsub$).pipe(map(res => serverLogBatchOf(res.data.listServerLogs.logBatch)));
    }

    listServerLogsSnapshot(
        props: { clusterUuid: string, serverUuid: string, serverId: number, maxLines: number }
    ): Observable<ServerLogBatch> {
        return this.api.clusterReqSub(new ClusterCtlProtoReq({
            listServerLogs: new ClusterCtlProtoListServerLogsReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                serverUuid: stringToBytes(props.serverUuid),
                serverIndex: props.serverId,
                initialLineCount: props.maxLines,
                snapshot: true,
            })
        }), new Subject<void>()).pipe(
            map(res => serverLogBatchOf(res.data.listServerLogs.logBatch)),
            takeWhile(res => !res.isEndOfFile, true),
        );
    }

    listClusterLogs(
        props: { clusterUuid: string, initialLineCount: number },
        unsub$: UnsubListener
    ): Observable<ClusterLogBatch> {
        return this.api.clusterReqSub(new ClusterCtlProtoReq({
            listClusterLogs: new ClusterCtlProtoListClusterLogsReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                initialLineCount: props.initialLineCount,
                snapshot: false,
            })
        }), unsub$).pipe(map(res => clusterLogBatchOf(res.data.listClusterLogs.logBatch)))
    }

    listClusterLogsSnapshot(
        props: { clusterUuid: string, maxLines: number },
    ): Observable<ClusterLogBatch> {
        return this.api.clusterReqSub(new ClusterCtlProtoReq({
            listClusterLogs: new ClusterCtlProtoListClusterLogsReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                initialLineCount: props.maxLines,
                snapshot: true,
            })
        }), new Subject<void>()).pipe(
            map(res => clusterLogBatchOf(res.data.listClusterLogs.logBatch)),
            takeWhile(res => !res.isEndOfFile, true),
        );
    }

    setupCluster(props: { clusterUuid: string, password: string }): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            setupCluster: new ClusterCtlProtoSetupClusterReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                password: props.password
            })
        }));
    }

    resetUserPassword(props: { clusterUuid: string, username: string, newPassword: string }): Observable<Ok> {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            resetUserPassword: new ClusterCtlProtoResetUserPasswordReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                username: props.username,
                newPassword: props.newPassword
            })
        }));
    }

    getStorageUsage(clusterUuid: string) {
        return this.api.clusterReqRes(new ClusterCtlProtoReq({
            getClusterStorageUsage: new ClusterCtlProtoGetClusterStorageUsageReq({
                clusterUuid: stringToBytes(clusterUuid)
            })
        })).pipe(map((res) => res.getClusterStorageUsage.bytesUsed));
    }

    takeSnapshot(clusterUuid: string) {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            takeSnapshot: new ClusterCtlProtoTakeSnapshotReq({
                clusterUuid: stringToBytes(clusterUuid)
            })
        }));
    }

    restoreCluster(props: { clusterUuid: string, snapshot: string }) {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            restoreCluster: new ClusterCtlProtoRestoreClusterReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                snapshotId: props.snapshot
            })
        }));
    }

    listSnapshots(clusterUuid: string) {
        return this.api.clusterReqRes(new ClusterCtlProtoReq({
            listSnapshots: new ClusterCtlProtoListSnapshotsReq({
                clusterUuid: stringToBytes(clusterUuid)
            })
        })).pipe(map((res) => res.listSnapshots.snapshot.map(snapshotOf)));
    }

    deleteSnapshot(props: { clusterUuid: string, snapshot: string }) {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            deleteSnapshot: new ClusterCtlProtoDeleteSnapshotReq({
                clusterUuid: stringToBytes(props.clusterUuid),
                snapshotId: props.snapshot
            })
        }));
    }

    checkClusterAutoPause(clusterUuid: string) {
        return this.api.clusterReqRes(new ClusterCtlProtoReq({
            checkClusterAutoPause: new ClusterCtlProtoCheckClusterAutoPauseReq({
                clusterUuid: stringToBytes(clusterUuid)
            })
        })).pipe(map(res => clusterAutoPauseInformationOf(res.checkClusterAutoPause)));
    }

    markClusterAsActive(clusterUuid: string) {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            markClusterAsActive: new ClusterCtlProtoMarkClusterAsActiveReq({
                clusterUuid: stringToBytes(clusterUuid)
            })
        }));
    }

    cloneCluster(sourceClusterUuid: string, destinationClusterId: string) {
        return this.api.clusterReq(new ClusterCtlProtoReq({
            cloneCluster: new ClusterCtlProtoCloneClusterReq({
                sourceClusterUuid: stringToBytes(sourceClusterUuid),
                destinationClusterId
            })
        }));
    }
}
